import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/campus/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MessageSquare, Phone, Calendar, Wallet, User, Home, Lock } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"];
const BLOCKS = [{k:"morning",l:"8AM–12PM"},{k:"afternoon",l:"12–4PM"},{k:"evening",l:"4–8PM"},{k:"night",l:"8–11PM"}];

export default function AmbassadorDashboard() {
  const { user, profile, loading, refresh } = useAuth();
  const [tab, setTab] = useState("overview");
  const [convos, setConvos] = useState([]);
  const [avail, setAvail] = useState({});
  const nav = useNavigate();

  useEffect(()=>{
    if (!loading && (!user || user.role !== "campus_ambassador")) { nav("/auth?mode=login"); return; }
    if (user) {
      api.myConversations().then(setConvos).catch(()=>{});
      api.getAvailability().then(a=>setAvail(a.weekly_schedule||{})).catch(()=>{});
    }
  }, [user, loading]);

  if (!user) return null;

  const status = profile?.verification_status || "pending";

  const Tab = ({id, icon, label}) => (
    <button data-testid={`tab-${id}`} onClick={()=>setTab(id)} className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm w-full text-left transition ${tab===id?"bg-[#2563EB] text-white":"text-slate-300 hover:bg-white/5"}`}>{icon}{label}</button>
  );

  return (
    <div className="min-h-screen bg-[#0F172A] text-white">
      <Navbar/>
      <div className="pt-20 px-6 md:px-12 max-w-7xl mx-auto grid md:grid-cols-[240px,1fr] gap-6 pb-20">
        <aside className="glass rounded-2xl p-3 h-fit md:sticky md:top-24 space-y-1">
          <Tab id="overview" icon={<Home size={16}/>} label="Overview"/>
          <Tab id="chats" icon={<MessageSquare size={16}/>} label="Chats"/>
          <Tab id="calls" icon={<Phone size={16}/>} label="Calls"/>
          <Tab id="availability" icon={<Calendar size={16}/>} label="Availability"/>
          <Tab id="earnings" icon={<Wallet size={16}/>} label="Earnings"/>
          <Tab id="profile" icon={<User size={16}/>} label="Profile"/>
        </aside>

        <main>
          {status === "pending" && <div className="bg-amber-500/15 border border-amber-500/30 text-amber-200 rounded-2xl p-4 mb-5 text-sm">⏳ Your profile is being verified. We'll notify you within 24–48 hours.</div>}
          {status === "rejected" && <div className="bg-red-500/15 border border-red-500/30 text-red-200 rounded-2xl p-4 mb-5 text-sm">❌ Application rejected: {profile?.rejection_reason}. Please update and re-submit.</div>}

          {tab==="overview" && (
            <div className="space-y-6">
              <h1 className="font-display text-3xl font-bold">Hi {user.full_name?.split(" ")[0]} 👋</h1>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard label="Rating" value={profile?.rating_avg || 0} suffix="⭐"/>
                <StatCard label="Total Chats" value={profile?.total_chats || 0}/>
                <StatCard label="Total Calls" value={profile?.total_calls || 0}/>
                <StatCard label="Earnings" value={profile?.earning_unlocked ? "Unlocked" : "Locked"}/>
              </div>
              <div className="glass rounded-2xl p-5">
                <div className="flex justify-between items-center mb-3">
                  <div className="font-semibold">Lifetime Calls Progress</div>
                  <div className="text-sm text-slate-400">{profile?.lifetime_calls || 0}/30 calls to unlock earnings</div>
                </div>
                <div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-amber-400" style={{width:`${Math.min(100, ((profile?.lifetime_calls||0)/30)*100)}%`}}/></div>
              </div>
            </div>
          )}
          {tab==="chats" && <ConvoList items={convos.filter(c=>c.type==="chat")} type="chat"/>}
          {tab==="calls" && <ConvoList items={convos.filter(c=>c.type==="audio_call")} type="audio_call"/>}
          {tab==="availability" && <AvailabilityTab avail={avail} setAvail={setAvail}/>}
          {tab==="earnings" && <EarningsTab profile={profile}/>}
          {tab==="profile" && <ProfileTab profile={profile} refresh={refresh}/>}
        </main>
      </div>
    </div>
  );
}

const StatCard = ({label,value,suffix=""}) => <div className="glass rounded-2xl p-5"><div className="text-xs text-slate-400">{label}</div><div className="font-display text-2xl font-bold mt-1">{value}{suffix}</div></div>;

function ConvoList({items, type}){
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-4 capitalize">{type==="chat"?"Chats":"Calls"}</h1>
      {items.length===0 && <div className="text-slate-400 text-sm">No {type==="chat"?"chats":"calls"} yet.</div>}
      <div className="space-y-3">{items.map(c=>(
        <Link key={c.id} to={type==="chat"?`/chat/${c.id}`:`/call/${c.id}`} className="block glass rounded-2xl p-4 hover:-translate-y-0.5 transition">
          <div className="flex justify-between"><div><div className="font-semibold">Student from {c.college_name}</div><div className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</div></div>
          <span className={`text-xs px-2 py-1 rounded-full capitalize ${c.status==="completed"?"bg-emerald-500/15 text-emerald-400":"bg-blue-500/15 text-blue-400"}`}>{c.status}</span></div>
        </Link>
      ))}</div>
    </div>
  );
}

function AvailabilityTab({avail, setAvail}) {
  const toggle = (day, block) => setAvail({...avail, [day]: {...(avail[day]||{}), [block]: !((avail[day]||{})[block])}});
  const save = async () => { try { await api.saveAvailability({weekly_schedule: avail}); toast.success("Availability saved"); } catch { toast.error("Failed"); } };
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-4">Weekly Availability</h1>
      <div className="glass rounded-2xl p-5 overflow-auto">
        <table className="w-full text-sm">
          <thead><tr><th className="text-left p-2">Day</th>{BLOCKS.map(b=><th key={b.k} className="p-2 text-xs text-slate-400">{b.l}</th>)}</tr></thead>
          <tbody>{DAYS.map(d=>(
            <tr key={d} className="border-t border-white/5"><td className="p-2 capitalize">{d}</td>
              {BLOCKS.map(b=>(<td key={b.k} className="p-2 text-center"><input type="checkbox" data-testid={`avail-${d}-${b.k}`} checked={!!(avail[d]||{})[b.k]} onChange={()=>toggle(d, b.k)} className="w-4 h-4 accent-[#2563EB]"/></td>))}
            </tr>
          ))}</tbody>
        </table>
      </div>
      <button data-testid="save-availability" onClick={save} className="mt-4 px-6 py-3 rounded-full bg-[#2563EB] hover:bg-blue-600 font-semibold">Save Availability</button>
    </div>
  );
}

function EarningsTab({profile}) {
  const [bank, setBank] = useState({holder_name:"",account_number:"",ifsc:"",bank_name:""});
  const unlocked = profile?.earning_unlocked;
  const save = async () => { try { await api.saveBank(bank); toast.success("Bank details saved"); } catch { toast.error("Failed"); } };
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-4">Earnings</h1>
      {!unlocked && (
        <div className="glass rounded-2xl p-8 text-center">
          <Lock className="mx-auto text-amber-400 mb-3" size={32}/>
          <div className="font-display text-xl font-bold mb-1">Earnings locked</div>
          <p className="text-slate-400 text-sm mb-4">Complete 30 lifetime audio calls to unlock direct earnings.</p>
          <div className="max-w-md mx-auto"><div className="h-3 bg-white/5 rounded-full overflow-hidden"><div className="h-full bg-amber-400" style={{width:`${Math.min(100,((profile?.lifetime_calls||0)/30)*100)}%`}}/></div>
          <div className="text-xs text-slate-400 mt-2">{profile?.lifetime_calls||0}/30 calls</div></div>
        </div>
      )}
      {unlocked && (
        <div className="space-y-4">
          <div className="glass rounded-2xl p-6 border-amber-400/30"><div className="font-display text-2xl font-bold">🎉 Earnings Unlocked!</div><p className="text-slate-400 text-sm mt-1">Payments will be sent directly to your bank account after each completed audio call.</p></div>
          <div className="glass rounded-2xl p-6 space-y-3">
            <h3 className="font-semibold">Bank Account Details</h3>
            <input placeholder="Account Holder Name" value={bank.holder_name} onChange={e=>setBank({...bank, holder_name:e.target.value})} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-2.5"/>
            <input placeholder="Account Number" value={bank.account_number} onChange={e=>setBank({...bank, account_number:e.target.value})} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-2.5"/>
            <input placeholder="IFSC Code" value={bank.ifsc} onChange={e=>setBank({...bank, ifsc:e.target.value})} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-2.5"/>
            <input placeholder="Bank Name" value={bank.bank_name} onChange={e=>setBank({...bank, bank_name:e.target.value})} className="w-full bg-[#1E293B] border border-white/10 rounded-lg px-4 py-2.5"/>
            <button onClick={save} className="px-5 py-2.5 rounded-full bg-[#2563EB] hover:bg-blue-600 font-semibold">Save</button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileTab({profile}){
  return (
    <div>
      <h1 className="font-display text-2xl font-bold mb-4">Profile</h1>
      <div className="glass rounded-2xl p-6 space-y-3 text-sm">
        <Row k="College" v={profile?.college_name}/><Row k="Course" v={profile?.course}/><Row k="Year" v={profile?.year}/><Row k="Status" v={profile?.verification_status}/><Row k="Bio" v={profile?.bio}/>
      </div>
    </div>
  );
}
const Row = ({k,v}) => <div className="flex justify-between gap-3"><span className="text-slate-400">{k}</span><span className="text-right">{v}</span></div>;
